/**
 * NumberPicker — React (web) implementation.
 *
 * All styling references the shared constants in NumberPicker.ts,
 * which in turn reference the colour/typography/spacing elements.
 * No raw hex values or pixel font sizes in this file.
 */

import React from 'react';
import { useStepBase } from './useStepBase';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { mouseHoverHandlers } from '../../elements/mouseHover.web';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { typography } from '../../elements/typography';
import { Icon } from '../Icon/Icon.web';
import {
  clampValue,
  numberPickerColorsFor,
  numberPickerAccessibleLabel,
  parseInlineValue,
  inlineValueAccentColor,
  type ColorStop,
  STEPPER_BUTTON_RADIUS,
  STEPPER_GAP,
  STEPPER_ICON_SIZE,
  STEPPER_ICON_DECREMENT,
  STEPPER_ICON_INCREMENT,
  VALUE_DISPLAY_MIN_WIDTH,
  VALUE_DISPLAY_HEIGHT,
  VALUE_DISPLAY_RADIUS,
  REFERENCE_COLUMN_WIDTH,
  INLINE_VALUE_VERTICAL_PADDING,
  INLINE_VALUE_BACKGROUND,
  INLINE_VALUE_FOCUS_BORDER_COLOR,
  INLINE_VALUE_BORDER_WIDTH,
  INLINE_VALUE_FOCUS_BORDER_WIDTH,
  INLINE_VALUE_TEXT_PADDING_HORIZONTAL,
  INLINE_VALUE_RADIUS,
  INLINE_VALUE_WIDTH,
  INLINE_VERTICAL_PADDING,
  INLINE_DIVIDER_HEIGHT,
  CENTERED_GAP,
  LABEL_SUBTITLE_GAP,
  INLINE_LABEL_TYPOGRAPHY,
  CENTERED_LABEL_TYPOGRAPHY,
  SUBTITLE_TYPOGRAPHY,
  INLINE_VALUE_TYPOGRAPHY,
  CENTERED_VALUE_TYPOGRAPHY,
  type NumberPickerLayoutType,
} from './NumberPicker';

const NUMBER_PICKER_FOCUS_STYLE_ID = 'galapagos-number-picker-value-focus-visible';
const NUMBER_PICKER_FOCUS_SELECTOR = '[data-galapagos-number-picker-value="true"]';
const NUMBER_PICKER_BUTTON_FOCUS_STYLE_ID = 'galapagos-number-picker-button-focus-visible';
const NUMBER_PICKER_BUTTON_FOCUS_SELECTOR = '[data-galapagos-number-picker-button="true"]';

// Web-only pointer and validation looks; native steppers have neither.
const STEPPER_BUTTON_HOVER_BACKGROUND = colors.button.disabled;
const INLINE_VALUE_INVALID_BORDER_COLOR = colors.button.critical;

// The web form sheet's stepper (Web/Stepper, Web/Day row): 44px controls (the
// pointer and touch target floor, matching the value field height) in both
// layouts, a value field framed in the form-field border at the large radius
// with no inset accent, and a 12/18 date line. Native keeps the shared
// catalog geometry.
const WEB_STEPPER_BUTTON_SIZE = VALUE_DISPLAY_HEIGHT;
const WEB_INLINE_VALUE_BORDER_COLOR = colors.stroke.input;
const WEB_INLINE_VALUE_RADIUS = radius.lg;
const WEB_INLINE_SUBTITLE_TYPOGRAPHY = typography.c2Regular;

// A typed value with anything but digits is not a count; the field keeps the
// last value instead of silently resetting it to the minimum.
const DIGITS_ONLY = /^\d*$/;

export interface WebNumberPickerProps {
  actionId?: string;
  decrementActionId?: string;
  incrementActionId?: string;
  label: string;
  value?: number;
  referenceValue?: number;
  referenceAccessibilityLabel?: string;
  subtitle?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  /**
   * Draws the error border on the value field and marks it invalid. Web only,
   * and only in the inline layout; the centered layout has no value field border.
   */
  invalid?: boolean;
  layout?: NumberPickerLayoutType;
  colorStops?: ColorStop[];
  onChange?: (value: number) => void;
  'data-testid'?: string;
  decrementTestID?: string;
  valueTestID?: string;
  incrementTestID?: string;
  'aria-label'?: string;
  decrementAccessibilityLabel: string;
  incrementAccessibilityLabel: string;
}

export function NumberPicker({
  actionId = 'ui.number-picker.change',
  decrementActionId = 'ui.number-picker.change',
  incrementActionId = 'ui.number-picker.change',
  label,
  value = 0,
  referenceValue,
  referenceAccessibilityLabel,
  subtitle,
  min = 0,
  max = 99,
  step = 1,
  disabled = false,
  invalid = false,
  layout = 'inline',
  colorStops,
  onChange,
  'data-testid': testId,
  decrementTestID,
  valueTestID,
  incrementTestID,
  'aria-label': ariaLabel,
  decrementAccessibilityLabel,
  incrementAccessibilityLabel,
}: WebNumberPickerProps) {
  React.useEffect(() => {
    ensureFocusVisibleStyle(NUMBER_PICKER_FOCUS_STYLE_ID, [NUMBER_PICKER_FOCUS_SELECTOR]);
    ensureFocusVisibleStyle(NUMBER_PICKER_BUTTON_FOCUS_STYLE_ID, [
      NUMBER_PICKER_BUTTON_FOCUS_SELECTOR,
    ]);
  }, []);
  const [focused, setFocused] = React.useState(false);
  const [hoveredButton, setHoveredButton] = React.useState<'decrement' | 'increment' | null>(null);
  const clamped = clampValue(value, min, max);
  const stepBase = useStepBase(clamped);
  const pending = clampValue(stepBase.latest, min, max);
  const isAtMin = disabled || pending <= min;
  const isAtMax = disabled || pending >= max;
  const pickerColors = numberPickerColorsFor(clamped, min, max, colorStops);

  const baseId = testId ?? `numberpicker-${label.replace(/\s+/g, '-').toLowerCase()}`;

  const handleDecrement = () => {
    const base = clampValue(stepBase.latest, min, max);
    if (disabled || base <= min) return;
    const next = Math.max(base - step, min);
    stepBase.advance(next);
    onChange?.(next);
  };

  const handleIncrement = () => {
    const base = clampValue(stepBase.latest, min, max);
    if (disabled || base >= max) return;
    const next = Math.min(base + step, max);
    stepBase.advance(next);
    onChange?.(next);
  };

  const labelTypo = layout === 'centered' ? CENTERED_LABEL_TYPOGRAPHY : INLINE_LABEL_TYPOGRAPHY;

  const stepperButtonStyle = (disabled: boolean, hovered: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: WEB_STEPPER_BUTTON_SIZE,
    height: WEB_STEPPER_BUTTON_SIZE,
    // Intrinsic size floor: the stepper buttons share a flex row with the label
    // and value field and must stay square when the label wraps.
    minWidth: WEB_STEPPER_BUTTON_SIZE,
    minHeight: WEB_STEPPER_BUTTON_SIZE,
    borderRadius: STEPPER_BUTTON_RADIUS,
    backgroundColor:
      hovered && !disabled ? STEPPER_BUTTON_HOVER_BACKGROUND : pickerColors.stepperButtonBackground,
    border: 'none',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? pickerColors.stepperButtonDisabledOpacity : 1,
    padding: 0,
  });

  // Inline value field: white, editable, with a uniform form-field border
  // (thicker/black on focus, alert red when invalid). The centered layout
  // keeps its filled valueDisplay.
  const inlineValueFieldWrapperStyle: React.CSSProperties = {
    position: 'relative',
    boxSizing: 'border-box',
    width: INLINE_VALUE_WIDTH,
    height: VALUE_DISPLAY_HEIGHT,
    borderRadius: WEB_INLINE_VALUE_RADIUS,
    backgroundColor: INLINE_VALUE_BACKGROUND,
    borderStyle: 'solid',
    borderColor: invalid
      ? INLINE_VALUE_INVALID_BORDER_COLOR
      : focused
        ? INLINE_VALUE_FOCUS_BORDER_COLOR
        : WEB_INLINE_VALUE_BORDER_COLOR,
    borderWidth: focused ? INLINE_VALUE_FOCUS_BORDER_WIDTH : INLINE_VALUE_BORDER_WIDTH,
  };

  const inlineValueFieldStyle: React.CSSProperties = {
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: WEB_INLINE_VALUE_RADIUS,
    backgroundColor: 'transparent',
    paddingLeft: INLINE_VALUE_TEXT_PADDING_HORIZONTAL,
    paddingRight: INLINE_VALUE_TEXT_PADDING_HORIZONTAL,
    paddingTop: INLINE_VALUE_VERTICAL_PADDING,
    paddingBottom: INLINE_VALUE_VERTICAL_PADDING,
    textAlign: 'center',
    fontFamily: INLINE_VALUE_TYPOGRAPHY.fontFamily,
    fontWeight: INLINE_VALUE_TYPOGRAPHY.fontWeight,
    fontSize: INLINE_VALUE_TYPOGRAPHY.fontSize,
    lineHeight: `${INLINE_VALUE_TYPOGRAPHY.lineHeight}px`,
    color: pickerColors.label,
  };

  // Read-only reference tile ("Last 7 days"): a filled green/blue tile (same
  // free/active scale as the editable accent) shown before the stepper,
  // centered within a shared-width column so it aligns with the "Last 7 days"
  // column header above it regardless of either element's own content width.
  const referenceColumnStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: REFERENCE_COLUMN_WIDTH,
    marginRight: STEPPER_GAP,
  };

  const referenceTileStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: VALUE_DISPLAY_MIN_WIDTH,
    height: VALUE_DISPLAY_HEIGHT,
    borderRadius: INLINE_VALUE_RADIUS,
    backgroundColor:
      referenceValue !== undefined ? inlineValueAccentColor(referenceValue, min) : 'transparent',
    paddingLeft: STEPPER_GAP,
    paddingRight: STEPPER_GAP,
    fontFamily: INLINE_VALUE_TYPOGRAPHY.fontFamily,
    fontWeight: INLINE_VALUE_TYPOGRAPHY.fontWeight,
    fontSize: INLINE_VALUE_TYPOGRAPHY.fontSize,
    lineHeight: `${INLINE_VALUE_TYPOGRAPHY.lineHeight}px`,
    color: pickerColors.label,
  };

  const accessibleName = numberPickerAccessibleLabel(label, ariaLabel);

  const stepperControls = (showValue: boolean) => (
    <div
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: STEPPER_GAP }}
      data-testid={`${baseId}-controls`}
    >
      <button
        data-analytics-action={decrementActionId}
        type="button"
        onClick={handleDecrement}
        disabled={isAtMin}
        aria-label={decrementAccessibilityLabel}
        aria-disabled={isAtMin}
        {...mouseHoverHandlers((over) => setHoveredButton(over ? 'decrement' : null))}
        style={stepperButtonStyle(isAtMin, hoveredButton === 'decrement')}
        data-testid={decrementTestID ?? `${baseId}-decrement`}
        data-galapagos-number-picker-button="true"
      >
        <Icon
          name={STEPPER_ICON_DECREMENT}
          size={STEPPER_ICON_SIZE}
          color={pickerColors.stepperButtonText}
          aria-hidden
        />
      </button>
      {showValue && (
        <div style={inlineValueFieldWrapperStyle} data-testid={`${baseId}-value-wrapper`}>
          <input
            data-analytics-action={actionId}
            data-galapagos-number-picker-value="true"
            type="text"
            inputMode="numeric"
            value={String(clamped)}
            onChange={(e) => {
              if (disabled || !DIGITS_ONLY.test(e.target.value.trim())) return;
              onChange?.(parseInlineValue(e.target.value, min, max));
            }}
            disabled={disabled}
            onFocus={(e) => {
              setFocused(true);
              e.target.select();
            }}
            onBlur={() => setFocused(false)}
            // A plain editable text field (the +/- are separate buttons); the
            // label carries the value and the polite live region below re-announces
            // it when a stepper press changes it without moving focus (Rule 4).
            aria-invalid={invalid || undefined}
            aria-label={`${accessibleName}, ${clamped}`}
            style={inlineValueFieldStyle}
            data-testid={valueTestID ?? `${baseId}-value`}
          />
        </div>
      )}
      <button
        data-analytics-action={incrementActionId}
        type="button"
        onClick={handleIncrement}
        disabled={isAtMax}
        aria-label={incrementAccessibilityLabel}
        aria-disabled={isAtMax}
        {...mouseHoverHandlers((over) => setHoveredButton(over ? 'increment' : null))}
        style={stepperButtonStyle(isAtMax, hoveredButton === 'increment')}
        data-testid={incrementTestID ?? `${baseId}-increment`}
        data-galapagos-number-picker-button="true"
      >
        <Icon
          name={STEPPER_ICON_INCREMENT}
          size={STEPPER_ICON_SIZE}
          color={pickerColors.stepperButtonText}
          aria-hidden
        />
      </button>
    </div>
  );

  if (layout === 'centered') {
    const centeredValueStyle: React.CSSProperties = {
      fontFamily: CENTERED_VALUE_TYPOGRAPHY.fontFamily,
      fontWeight: CENTERED_VALUE_TYPOGRAPHY.fontWeight,
      fontSize: CENTERED_VALUE_TYPOGRAPHY.fontSize,
      lineHeight: `${CENTERED_VALUE_TYPOGRAPHY.lineHeight}px`,
      color: pickerColors.valueText,
      textAlign: 'center',
    };

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: CENTERED_GAP,
        }}
        data-testid={baseId}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: LABEL_SUBTITLE_GAP,
          }}
        >
          <span
            style={{
              fontFamily: labelTypo.fontFamily,
              fontWeight: labelTypo.fontWeight,
              fontSize: labelTypo.fontSize,
              lineHeight: `${labelTypo.lineHeight}px`,
              color: pickerColors.label,
              textAlign: 'center',
            }}
          >
            {label}
          </span>
          {subtitle && (
            <span
              style={{
                fontFamily: SUBTITLE_TYPOGRAPHY.fontFamily,
                fontWeight: SUBTITLE_TYPOGRAPHY.fontWeight,
                fontSize: SUBTITLE_TYPOGRAPHY.fontSize,
                lineHeight: `${SUBTITLE_TYPOGRAPHY.lineHeight}px`,
                color: pickerColors.subtitle,
                textAlign: 'center',
              }}
            >
              {subtitle}
            </span>
          )}
        </div>
        <div
          role="status"
          aria-live="polite"
          aria-label={`${accessibleName}, ${clamped}`}
          style={{
            ...centeredValueStyle,
            backgroundColor: pickerColors.valueBackground,
            borderRadius: VALUE_DISPLAY_RADIUS,
            paddingTop: INLINE_VERTICAL_PADDING,
            paddingBottom: INLINE_VERTICAL_PADDING,
            paddingLeft: CENTERED_GAP,
            paddingRight: CENTERED_GAP,
            minWidth: VALUE_DISPLAY_MIN_WIDTH * 2,
          }}
          data-testid={valueTestID ?? `${baseId}-value`}
        >
          {clamped}
        </div>
        {stepperControls(false)}
      </div>
    );
  }

  // Inline layout
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: INLINE_VERTICAL_PADDING,
        paddingBottom: INLINE_VERTICAL_PADDING,
        borderBottom: `${INLINE_DIVIDER_HEIGHT}px solid ${pickerColors.divider}`,
      }}
      data-testid={baseId}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: LABEL_SUBTITLE_GAP }}>
        <span
          style={{
            fontFamily: labelTypo.fontFamily,
            fontWeight: labelTypo.fontWeight,
            fontSize: labelTypo.fontSize,
            lineHeight: `${labelTypo.lineHeight}px`,
            color: pickerColors.label,
          }}
        >
          {label}
        </span>
        {subtitle && (
          <span
            style={{
              fontFamily: WEB_INLINE_SUBTITLE_TYPOGRAPHY.fontFamily,
              fontWeight: WEB_INLINE_SUBTITLE_TYPOGRAPHY.fontWeight,
              fontSize: WEB_INLINE_SUBTITLE_TYPOGRAPHY.fontSize,
              lineHeight: `${WEB_INLINE_SUBTITLE_TYPOGRAPHY.lineHeight}px`,
              color: pickerColors.subtitle,
            }}
          >
            {subtitle}
          </span>
        )}
      </div>
      {referenceValue !== undefined && (
        <div style={referenceColumnStyle} data-testid={`${baseId}-reference-column`}>
          <div
            aria-label={
              referenceAccessibilityLabel
                ? `${accessibleName}, ${referenceAccessibilityLabel} ${referenceValue}`
                : `${accessibleName}, ${referenceValue}`
            }
            style={referenceTileStyle}
            data-testid={`${baseId}-reference`}
          >
            {referenceValue}
          </div>
        </div>
      )}
      {stepperControls(true)}
      {/* A stepper press keeps focus on the +/- button, so the editable field's
          own spinbutton value is not re-announced. This visually-hidden polite
          live region speaks the updated count on every change (ADR-025 Rule 4). */}
      <span
        role="status"
        aria-live="polite"
        data-testid={`${baseId}-value-announce`}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {`${accessibleName}, ${clamped}`}
      </span>
    </div>
  );
}
